/**
 * Dropdown menu for user lifecycle actions
 */

import type { UserStatus } from "@eoln/userpool/types";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { Ban, CheckCircle, MoreVertical, Play, Power, PowerOff, Trash2, Unlock } from "lucide-react";
import { Fragment } from "react";

interface UserActionsMenuProps {
  status: UserStatus;
  onActivate?: () => void;
  onSuspend?: () => void;
  onUnsuspend?: () => void;
  onDisable?: () => void;
  onEnable?: () => void;
  onUnlock?: () => void;
  onDelete?: () => void;
  isLoading?: boolean;
}

export function UserActionsMenu({
  status,
  onActivate,
  onSuspend,
  onUnsuspend,
  onDisable,
  onEnable,
  onUnlock,
  onDelete,
  isLoading,
}: UserActionsMenuProps) {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
        disabled={isLoading}
      >
        <MoreVertical className="h-5 w-5" />
      </MenuButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:ring-gray-700">
          <div className="py-1">
            {/* Activate - only for pending users */}
            {status === "PENDING" && onActivate && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={onActivate}
                    className={`${focus ? "bg-gray-100 dark:bg-gray-700" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                  >
                    <CheckCircle className="mr-3 h-4 w-4 text-green-500" />
                    Activate
                  </button>
                )}
              </MenuItem>
            )}

            {/* Suspend - for active users */}
            {status === "ACTIVE" && onSuspend && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={onSuspend}
                    className={`${focus ? "bg-gray-100 dark:bg-gray-700" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                  >
                    <Ban className="mr-3 h-4 w-4 text-orange-500" />
                    Suspend
                  </button>
                )}
              </MenuItem>
            )}

            {/* Unsuspend - for suspended users */}
            {status === "SUSPENDED" && onUnsuspend && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={onUnsuspend}
                    className={`${focus ? "bg-gray-100 dark:bg-gray-700" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                  >
                    <Play className="mr-3 h-4 w-4 text-green-500" />
                    Unsuspend
                  </button>
                )}
              </MenuItem>
            )}

            {/* Disable - for active or suspended users */}
            {(status === "ACTIVE" || status === "SUSPENDED") && onDisable && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={onDisable}
                    className={`${focus ? "bg-gray-100 dark:bg-gray-700" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                  >
                    <PowerOff className="mr-3 h-4 w-4 text-gray-500" />
                    Disable
                  </button>
                )}
              </MenuItem>
            )}

            {/* Enable - for disabled users */}
            {status === "DISABLED" && onEnable && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={onEnable}
                    className={`${focus ? "bg-gray-100 dark:bg-gray-700" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                  >
                    <Power className="mr-3 h-4 w-4 text-green-500" />
                    Enable
                  </button>
                )}
              </MenuItem>
            )}

            {/* Unlock - for locked users */}
            {status === "LOCKED" && onUnlock && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={onUnlock}
                    className={`${focus ? "bg-gray-100 dark:bg-gray-700" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                  >
                    <Unlock className="mr-3 h-4 w-4 text-blue-500" />
                    Unlock
                  </button>
                )}
              </MenuItem>
            )}

            {/* Delete - always available */}
            {onDelete && (
              <>
                <div className="border-t border-gray-100 dark:border-gray-700" />
                <MenuItem>
                  {({ focus }) => (
                    <button
                      type="button"
                      onClick={onDelete}
                      className={`${focus ? "bg-gray-100 dark:bg-gray-700" : ""} flex w-full items-center px-4 py-2 text-sm text-red-600 dark:text-red-400`}
                    >
                      <Trash2 className="mr-3 h-4 w-4" />
                      Delete
                    </button>
                  )}
                </MenuItem>
              </>
            )}
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
}
